import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { translate as translateConstructor, Spinner } from '@haldor/ui';
import { Form, FormSpy } from 'react-final-form';
import { Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { getFormTemplatesByType } from 'actions/templates';
import FormFieldRenderer from 'components/FormFieldRenderer';
import { FormPrompt } from 'components/Inputs/FormPrompt';
import './DisplayForm.scss';
var DisplayForm = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var type = _ref.type,
    providedFields = _ref.fields,
    onSubmit = _ref.onSubmit,
    _ref$initialValues = _ref.initialValues,
    initialValues = _ref$initialValues === void 0 ? [] : _ref$initialValues,
    onSuccess = _ref.onSuccess,
    onCancel = _ref.onCancel,
    _ref$hideSaveButton = _ref.hideSaveButton,
    hideSaveButton = _ref$hideSaveButton === void 0 ? false : _ref$hideSaveButton;
  var formPromptRef = useRef();
  var formApiRef = useRef(null);

  // Expose confirmClose to parent via ref
  useImperativeHandle(ref, function () {
    return {
      confirmClose: function () {
        var _confirmClose = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
          var form, isDirty, confirmed;
          return _regeneratorRuntime.wrap(function (_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                form = formApiRef.current;
                isDirty = form ? form.getState().dirty && !form.getState().submitSucceeded || form.getState().dirtySinceLastSubmit : false;
                if (isDirty) {
                  _context.next = 1;
                  break;
                }
                return _context.abrupt("return", true);
              case 1:
                if (!formPromptRef.current) {
                  _context.next = 3;
                  break;
                }
                _context.next = 2;
                return formPromptRef.current.showPrompt();
              case 2:
                confirmed = _context.sent;
                return _context.abrupt("return", !!confirmed);
              case 3:
                return _context.abrupt("return", true);
              case 4:
              case "end":
                return _context.stop();
            }
          }, _callee);
        }));
        function confirmClose() {
          return _confirmClose.apply(this, arguments);
        }
        return confirmClose;
      }()
    };
  }, []);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    submitting = _useState4[0],
    setSubmitting = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    submitError = _useState6[0],
    setSubmitError = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    submitSuccess = _useState8[0],
    setSubmitSuccess = _useState8[1];
  var dispatch = useDispatch();
  var history = useHistory();
  var allTemplatesByType = useSelector(function (state) {
    return state.Templates.templatesByType || {};
  });
  var translations = useSelector(function (state) {
    return state.Languages.translations;
  });
  var translate = translateConstructor(translations);

  // Pre-compute existing field results for efficient lookups when updating
  var existingResultsByField = useMemo(function () {
    var map = {};
    // Handle initialValues as either array of results or object with field values
    if (Array.isArray(initialValues)) {
      initialValues.forEach(function (result) {
        if (result && result.fieldName) {
          map[result.fieldName] = result;
        }
      });
    } else if (initialValues && _typeof(initialValues) === 'object') {
      // If initialValues is an object { fieldName: value }, convert to result format
      Object.keys(initialValues).forEach(function (fieldName) {
        map[fieldName] = {
          fieldName: fieldName,
          value: initialValues[fieldName]
        };
      });
    }
    return map;
  }, [initialValues]);

  // Convert initialValues to object format for React Final Form
  var formInitialValues = useMemo(function () {
    if (Array.isArray(initialValues)) {
      // Convert array of results to object { fieldName: value }
      var values = {};
      initialValues.forEach(function (result) {
        if (result && result.fieldName) {
          values[result.fieldName] = result.value;
        }
      });
      return values;
    } else if (initialValues && _typeof(initialValues) === 'object') {
      // Already in object format
      return initialValues;
    }
    return {};
  }, [initialValues]);

  // Prepare templatesByType depending on what was provided
  // Accept either an array of fields OR an object { id, fields: [...] }
  var templatesByType;
  if (providedFields) {
    if (Array.isArray(providedFields)) {
      templatesByType = {
        fields: providedFields,
        id: null
      };
    } else if (Array.isArray(providedFields.fields)) {
      templatesByType = {
        fields: providedFields.fields,
        id: providedFields.id || null
      };
    } else {
      templatesByType = {
        fields: [],
        id: null
      };
    }
  } else {
    templatesByType = allTemplatesByType[type];
  }
  useEffect(function () {
    var fetchTemplates = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              if (!(!providedFields && type && !allTemplatesByType[type])) {
                _context2.next = 2;
                break;
              }
              setLoading(true);
              _context2.next = 1;
              return dispatch(getFormTemplatesByType(type));
            case 1:
              setLoading(false);
              _context2.next = 3;
              break;
            case 2:
              setLoading(false);
            case 3:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      return function fetchTemplates() {
        return _ref2.apply(this, arguments);
      };
    }();
    fetchTemplates();
  }, [type, providedFields, dispatch, allTemplatesByType]);
  var handleSubmit = function handleSubmit(values) {
    return new Promise(function (resolve) {
      var _templatesByType;
      setSubmitting(true);
      setSubmitError(null); // Clear previous errors

      // Collect field results similar to FormInstance
      var fieldResults = [];
      Object.keys(values).forEach(function (key) {
        var templateField = fields.find(function (t) {
          return t.name === key;
        });
        var existingResult = existingResultsByField[key];
        var result = {
          fieldName: key,
          value: values[key],
          FieldTemplateId: templateField != null ? templateField.id : null
        };
        if ((existingResult === null || existingResult === void 0 ? void 0 : existingResult.id) != null) {
          result.id = existingResult.id;
        }
        fieldResults.push(result);
      });
      var data = {
        fieldResults: fieldResults,
        status: 'SUBMITTED',
        formTemplateId: ((_templatesByType = templatesByType) === null || _templatesByType === void 0 ? void 0 : _templatesByType.id) || null,
        type: type || null
      };

      // Call onSubmit if provided
      if (onSubmit) {
        onSubmit(data).then(function () {
          setSubmitting(false);
          setSubmitError(null);
          setSubmitSuccess(true);

          // Call onSuccess callback if provided (for closing modals, etc.)
          if (onSuccess) {
            setTimeout(function () {
              onSuccess({
                success: true
              });
            }, 1000); // Delay to allow user to see success message
          }
          resolve();
        })["catch"](function (error) {
          var _error$response;
          setSubmitting(false);
          setSubmitSuccess(false);
          var errorMessage = (error === null || error === void 0 || (_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message) || (error === null || error === void 0 ? void 0 : error.message) || 'Could not submit form';
          setSubmitError(errorMessage);
          resolve({
            error: errorMessage
          });
        });
      } else {
        setSubmitting(false);
        resolve();
      }
    });
  };
  if (loading) {
    return /*#__PURE__*/React.createElement(Spinner, {
      center: true
    });
  }

  // No template found and no fields provided
  if (!templatesByType && !providedFields) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      color: "text.secondary"
    }, translate('No form templates found for this type')));
  }

  // Resolve raw fields depending on provided shape
  var rawFields = providedFields ? Array.isArray(providedFields) ? providedFields : providedFields.fields || [] : templatesByType.fields || templatesByType.FieldTemplates || [];

  // Sort fields by sortOrder - use fields prop if provided, otherwise from template
  var fields = _toConsumableArray(rawFields);
  fields.sort(function (a, b) {
    return (a.sortOrder || 0) > (b.sortOrder || 0) ? 1 : -1;
  });

  // Ensure each field has a name property
  fields.forEach(function (field, index) {
    if (!field.name) {
      field.name = field.fieldName || "field-".concat(index);
    }
  });
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: handleSubmit,
    initialValues: formInitialValues,
    render: function render(_ref3) {
      var handleSubmit = _ref3.handleSubmit,
        form = _ref3.form,
        submitSucceeded = _ref3.submitSucceeded,
        modifiedSinceLastSubmit = _ref3.modifiedSinceLastSubmit,
        dirty = _ref3.dirty;
      // Handler for Cancel button: do NOT show the unsaved-changes prompt when user clicks Cancel
      var handleCancel = /*#__PURE__*/function () {
        var _ref4 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
          return _regeneratorRuntime.wrap(function (_context3) {
            while (1) switch (_context3.prev = _context3.next) {
              case 0:
                if (onCancel) {
                  onCancel();
                } else {
                  history.goBack();
                }
              case 1:
              case "end":
                return _context3.stop();
            }
          }, _callee3);
        }));
        return function handleCancel() {
          return _ref4.apply(this, arguments);
        };
      }();
      // Keep a reference to the form API so parent can query dirty state
      formApiRef.current = form;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("form", {
        onSubmit: handleSubmit
      }, /*#__PURE__*/React.createElement(FormPrompt, {
        ref: formPromptRef,
        hasUnsavedChanges: form.getState().dirty && !form.getState().submitSucceeded || form.getState().dirtySinceLastSubmit,
        formPromptTitle: translate('are-you-sure'),
        formPromptMessage: translate('close-form-prompt'),
        formPromptCancel: translate('Cancel'),
        formPromptOk: translate('Ok'),
        currentPathContext: window.location.pathname
      }), submitSuccess && /*#__PURE__*/React.createElement(Box, {
        sx: {
          mb: 2,
          px: 2,
          py: 1,
          borderRadius: 1,
          backgroundColor: '#4BAF4D'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        sx: {
          color: '#FFFFFF',
          fontWeight: 500
        }
      }, translate('Form submitted successfully'))), submitError && /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        color: "error.main",
        sx: {
          mb: 2
        }
      }, submitError), /*#__PURE__*/React.createElement(Box, {
        sx: {
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }
      }, fields.map(function (field, index) {
        return /*#__PURE__*/React.createElement(FormFieldRenderer, {
          key: index,
          field: field,
          index: index,
          translate: translate
        });
      })), !hideSaveButton && /*#__PURE__*/React.createElement(Box, {
        sx: {
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: 3,
          justifyContent: 'space-between'
        }
      }, /*#__PURE__*/React.createElement(Button, {
        type: "submit",
        variant: "contained",
        color: "primary",
        disabled: submitting
      }, submitting ? translate('Submitting') : translate('Send')), /*#__PURE__*/React.createElement(Button, {
        type: "button",
        variant: "outlined",
        onClick: handleCancel,
        disabled: submitting
      }, translate('Cancel')), /*#__PURE__*/React.createElement(FormSpy, {
        subscription: {
          submitSucceeded: true,
          modifiedSinceLastSubmit: true,
          submitError: true
        }
      }, function (_ref5) {
        var submitSucceeded = _ref5.submitSucceeded,
          modifiedSinceLastSubmit = _ref5.modifiedSinceLastSubmit,
          formError = _ref5.submitError;
        return /*#__PURE__*/React.createElement(React.Fragment, null, submitSucceeded && !modifiedSinceLastSubmit && !submitError ? /*#__PURE__*/React.createElement(Typography, {
          variant: "body2",
          color: "success.main",
          sx: {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }
        }, "\u2713 ", translate('saved')) : null);
      }))));
    }
  }));
});
export default DisplayForm;